import type { categoryId, IMeasurementUnit, IUnitCategory, unitId } from "../../interfaces/interfaces";

export class UnitConverter {
	private _categories: Map<categoryId, IUnitCategory> = new Map();
	private _units: Map<unitId, IMeasurementUnit> = new Map();

	constructor() {
		this.initializeUnits();
	}

	private initializeUnits(): void {
		const pressureUnits: IMeasurementUnit[] = [
			{
				id: 'bar',
				category: 'pressure',
				toBase: (value) => value,
				fromBase: (value) => value
			},
			{
				id: 'atm',
				category: 'pressure',
				toBase: (value) => value * 1.01325,
				fromBase: (value) => value / 1.01325
			},
			{
				id: 'mpa',
				category: 'pressure',
				system: 'SI',
				toBase: (value) => value * 10,
				fromBase: (value) => value / 10
			},
			{
				id: 'kgfPerCm2',
				category: 'pressure',
				toBase: (value) => value / 1.019716,
				fromBase: (value) => value * 1.019716
			},
			{
				id: 'psi',
				category: 'pressure',
				system: 'BIS',
				toBase: (value) => value * (1 / 14.503773773),
				fromBase: (value) => value * 14.5038
			}
		];

		const temperatureUnits: IMeasurementUnit[] = [
			{
				id: 'celsius',
				category: 'temperature',
				system: 'SI',
				toBase: (value) => value,
				fromBase: (value) => value
			},
			{
				id: 'fahrenheit',
				category: 'temperature',
				system: 'BIS',
				toBase: (value) => (value - 32) * 5 / 9,
				fromBase: (value) => value * 9 / 5 + 32
			},
			{
				id: 'kelvin',
				category: 'temperature',
				toBase: (value) => value - 273.15,
				fromBase: (value) => value + 273.15
			},
			{
				id: 'rankine',
				category: 'temperature',
				system: 'BIS',
				toBase: (value) => (value - 491.67) * 5 / 9,
				fromBase: (value) => value * 9 / 5 + 491.67
			}
		];

		const lengthUnits: IMeasurementUnit[] = [
			{
				id: 'millimeter',
				category: 'length',
				toBase: (value) => value / 1000,
				fromBase: (value) => value * 1000
			},
			{
				id: 'meter',
				category: 'length',
				system: 'SI',
				toBase: (value) => value,
				fromBase: (value) => value
			},
			{
				id: 'kilometer',
				category: 'length',
				toBase: (value) => value * 1000,
				fromBase: (value) => value / 1000
			},
		];

		const absolutePermeabilityUnits: IMeasurementUnit[] = [
			{
				id: 'millidarcy',
				category: 'absolute_permeability',
				system: 'SI',
				toBase: (value) => value,
				fromBase: (value) => value
			},
			{
				id: 'darcy',
				category: 'absolute_permeability',
				toBase: (value) => value * 1000,
				fromBase: (value) => value / 1000
			},
		];

		const surfaceTensionUnits: IMeasurementUnit[] = [
			{
				id: 'dynescm',
				category: 'surface_tension',
				system: 'SI',
				toBase: (value) => value,
				fromBase: (value) => value
			},
		];

		const qUnits: IMeasurementUnit[] = [
			{
				id: 'thousand_m3_day',
				category: 'q',
				system: 'SI',
				toBase: (value) => value,
				fromBase: (value) => value
			},
			{
				id: 'mln_m3_day',
				category: 'q',
				system: 'SI',
				toBase: (value) => value * 1000,
				fromBase: (value) => value / 1000
			},
		];

		const categories: IUnitCategory[] = [
			{
				id: 'temperature',
				units: temperatureUnits,
				baseUnit: 'celsius',
			},
			{
				id: 'pressure',
				units: pressureUnits,
				baseUnit: 'bar'
			},
			{
				id: 'length',
				units: lengthUnits,
				baseUnit: 'meter'
			},
			{
				id: 'absolute_permeability',
				units: absolutePermeabilityUnits,
				baseUnit: 'millidarcy'
			},
			{
				id: 'surface_tension',
				units: surfaceTensionUnits,
				baseUnit: 'dynescm'
			},
			{
				id: 'q',
				units: qUnits,
				baseUnit: 'thousand_m3_day'
			}
		];

		categories.forEach(category => {
			this._categories.set(category.id, category);
			category.units.forEach(unit => {
				this._units.set(unit.id, unit);
			});
		});

	}

	convert(value: number, fromUnitId: unitId | null, toUnitId: unitId | null): number {
		if (!fromUnitId || !toUnitId) {
			return value;
		}

		const fromUnit = this._units.get(fromUnitId);
		const toUnit = this._units.get(toUnitId);

		if (!fromUnit || !toUnit) {
			// throw new Error('Единица измерения не найдена');
			return value;
		}

		if (fromUnit.category !== toUnit.category) {
			throw new Error('Нельзя конвертировать между разными категориями');
			// return value;
		}

		const baseValue = fromUnit.toBase(value);

		const currentValue = toUnit.fromBase(baseValue);

		// const absValue = Math.abs(currentValue);
		// const decimalPlaces = absValue > 1 ? 2 : 4;

		return currentValue;
		// return roundTo(currentValue, decimalPlaces);
	}

	getCategories(): IUnitCategory[] {
		return Array.from(this._categories.values());
	}

	getBaseUnitForCategory(categoryId: categoryId): unitId | null {
		return this._categories.get(categoryId)?.baseUnit ?? null;
	}

	getUnitsByCategory(categoryId: categoryId | null): IMeasurementUnit[] {
		if (!categoryId) {
			return [];
		}
		const category = this._categories.get(categoryId);
		return category ? category.units : [];
	}

	getUnit(unitId: unitId): IMeasurementUnit | undefined {
		return this._units.get(unitId);
	}

	getFirstUnitBySystem(categoryId: categoryId, system: 'SI' | 'BIS'): IMeasurementUnit | undefined {
		const category = this._categories.get(categoryId);
		if (!category) return undefined;
		const unit = category.units?.find(u => u.system === system);
		return unit;
	}
}

export const unitConverter = new UnitConverter();